import { yupResolver } from '@hookform/resolvers/yup';
import { Form } from '@openx/components-core/lib/Form/Form';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField';
import { scrollToFirstVisibleErrorInForm } from '@openx/components-core/lib/utils/scrollToFirstErrorInForm';
import { PackageFields, type PackageFormState } from '@openx/types/package';
import { TargetingFields } from '@openx/types/targeting/targeting';
import { type JSX, useEffect, useMemo } from 'react';
import { type SubmitErrorHandler, type UseFormSetError, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PackageFormSections } from './sections/PackageFormSections';
import { useTargetingBlockingRules } from './targetingRules/useTargetingBlockingRules';
import { formStateToPackage, normalizeEstimates, normalizeGeographic } from './utils/formStateConverter';
import { getValidationSchema } from './validationSchema';

export type PackageFormProps = {
  goBack?: () => void;
  initialValues: PackageFormState;
  currency: string;
  onSubmit?: (formValues: PackageFormState, setError: UseFormSetError<PackageFormState>) => void;
  isSubmitting: boolean;
  setSelectedAccountId?: (value: string) => void;
  accountUid?: string;
  isEdit?: boolean;
  resetFormData?: PackageFormState;
  openXAgentTools?: (isSubmitting: boolean, formValues: PackageFormState) => JSX.Element;
};

export const PackageForm = ({
  goBack,
  initialValues,
  currency,
  onSubmit,
  isSubmitting,
  setSelectedAccountId,
  accountUid,
  isEdit = false,
  resetFormData,
  openXAgentTools,
}: PackageFormProps): JSX.Element => {
  const { t } = useTranslation();

  const formMethods = useForm<PackageFormState>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: yupResolver(getValidationSchema(t), {}, { rawValues: true }),
  });

  useEffect(() => {
    if (!accountUid) {
      return;
    }

    formMethods.setValue('account_uid', accountUid);
  }, [accountUid, formMethods]);

  const observableFormValues = useWatch({
    control: formMethods.control,
  });

  const observablePackage = useMemo(
    () => formStateToPackage(observableFormValues as PackageFormState),
    [observableFormValues]
  );

  const observableTargeting = useMemo(
    () => ({
      ...(observablePackage[PackageFields.TARGETING] ?? {}),
      [TargetingFields.URL_TARGETING]: observablePackage[TargetingFields.URL_TARGETING] ?? null,
    }),
    [observablePackage]
  );

  const { fieldsWithErrors, isTargetingBlockingRulesValidationPending } = useTargetingBlockingRules({
    targeting: observableTargeting,
  });

  const scrollToFirstFormError = (): void => {
    const formElement = document.querySelector('[data-test="package-form"]');

    scrollToFirstVisibleErrorInForm({
      root: formElement ?? document,
    });
  };

  const onInvalidSubmit: SubmitErrorHandler<PackageFormState> = () => {
    scrollToFirstFormError();
  };

  const onSubmitForm = (values: PackageFormState): void => {
    if (isTargetingBlockingRulesValidationPending || Object.keys(fieldsWithErrors).length > 0) {
      scrollToFirstFormError();

      return;
    }

    // normalize estimates and location radius to avoid deal form state changed
    values[PackageFields.ESTIMATES] = normalizeEstimates(values[PackageFields.ESTIMATES]);
    if (values[PackageFields.TARGETING]) {
      values[PackageFields.TARGETING][TargetingFields.GEOGRAPHIC] = normalizeGeographic(
        values[PackageFields.TARGETING]?.[TargetingFields.GEOGRAPHIC]
      );
    }

    onSubmit?.(values, formMethods.setError);
  };

  return (
    <Form
      methods={formMethods}
      data-test="package-form"
      autoComplete="off"
      noValidate
      onSubmit={formMethods.handleSubmit(onSubmitForm, onInvalidSubmit)}
      name="package"
    >
      <RHFTextField type="hidden" name="account_uid" />
      <PackageFormSections
        initialValues={initialValues}
        goBack={goBack}
        isSubmitting={isSubmitting}
        currency={currency}
        setSelectedAccountId={setSelectedAccountId}
        isEdit={isEdit}
        resetFormData={resetFormData}
        openXAgentTools={openXAgentTools}
      />
    </Form>
  );
};
